import * as _ from 'lodash';
import PropTypes from 'prop-types';
import type { ChangeEvent } from 'react';
import { Component } from 'react';
import { Button as BSButton, Checkbox, ProgressBar } from 'react-bootstrap';
import { connect } from 'react-redux';

import Locator from '../../../../shared/Locator';
import { window } from '../../../globals';
import TeamsModule from '../../../redux/teams';
import VCSModule from '../../../redux/vcs';
import type { VCSImportState } from '../../../redux/vcs/types';
import type { IAsyncStatus, Project, Repository, ServiceFilter, ServiceQueryParameters, Team } from '../../../types';
import utils from '../../../utils';
import Button, { BUTTON_SIZE, BUTTON_TYPE } from '../../generic/Button';
import { icons, IconText } from '../../generic/Icon';
import Link from '../../generic/Link';
import Loader from '../../generic/Loader';
import ModalBulkImport from '../../modals/ModalBulkImport';
import { teamsUserCanAddProject } from '../SelectTeam';
import ModalServiceConnect from './ModalServiceConnect';
import ModalTeamAssignment from './ModalTeamAssignment';
import ServiceImportHeader from './ServiceImportHeader';
import ServiceImportRespositories from './ServiceImportRepositories';
import type { ProjectImportOptions, ServiceVCS } from './types';
import { ServiceMeta } from './types';
import { getAuthorizeServiceURL } from '../../../helpers/services';
import { completeGithubAppInstallationProcess, hasGithubAppInstallation } from './utils';
import styles from './ServiceImport.module.less';
import { isFeatureEnabled } from '../../../helpers/features';
import { HYBRID_PROJECTS } from '../../../../shared/featureFlags/flagNames';

type ServiceImportState = {
  send_badge_pr: boolean;
  showConnectModal: boolean;
  show_bulk_import: boolean;
  selectedTeams: Team[];
  showTeamSelectionModal: boolean;
  errorFromQueryParameters: string;
};

type ReduxProps = {
  status: IAsyncStatus;
  importStatus: VCSImportState;
  repositories: Repository[];
  selectedRepositories: Repository[];
  filters: ServiceFilter[];
  query: ServiceQueryParameters;
  connected: boolean;
  privateAccess: boolean;
  instances: string[];
  allTeams: Team[];
  fetchAllTeams: (filters: object) => void;
  fetchInstances: (service: string, instanceToSelect?: string) => Promise<void>;
  fetchFilters: (service: string, selectedInstance: string) => Promise<void>;
  fetchRepositories: (service: string, query: ServiceQueryParameters) => Promise<void>;
  importRepositories: (
    service: string,
    repos: Repository[],
    options: ProjectImportOptions,
    instanceName: string,
    filterValue?: string,
  ) => Promise<void>;
  resetImportStatus: () => void;
  setQueryParameters: (query: ServiceQueryParameters) => void;
  setFilter: (filterType: string, filterValue: string) => void;
  setInstance: (instance: string) => void;
  setBranch: (repo: Repository, branch: string) => void;
  selectRepository: (repo: Repository) => void;
  disconnectService: (service: string, selectedInstance: string) => Promise<void>;
};

type ServiceImportProps = ReduxProps & {
  service: Project['vcs_host'] | 'slack';
  location?: any;
};

type AuthorizeUrlParams = {
  service: Project['vcs_host'] | 'slack';
  nextUrl: string;
  privateAccess?: boolean;
  serviceConnect?: boolean;
};

const FETCH_REPOS_DEBOUNCE_TIMEOUT = 750;

class ServiceImport extends Component<ServiceImportProps, ServiceImportState> {
  // eslint-disable-next-line react/static-property-placement
  static contextTypes = {
    // eslint-disable-next-line react/forbid-prop-types
    router: PropTypes.object.isRequired,
  };

  constructor(props: ServiceImportProps) {
    super(props);
    this.state = {
      send_badge_pr: true,
      showConnectModal: false,
      show_bulk_import: false,
      selectedTeams: [],
      showTeamSelectionModal: false,
      errorFromQueryParameters: '',
    };
  }

  async componentDidMount() {
    const { setup_action: setupAction, installation_id: installationId } = this.props.location.query;
    completeGithubAppInstallationProcess(setupAction, installationId);
    const queryParams = this.props.location.query;
    if (queryParams.page) {
      queryParams.page = Number(queryParams.page);
    }
    if (queryParams.pageSize) {
      queryParams.pageSize = Number(queryParams.pageSize);
    }

    // We don't want to pollute the redux store with this error, so we keep it in the component store
    if (queryParams.error) {
      this.setState({ errorFromQueryParameters: queryParams.error });
      this.context.router.replace({
        pathname: this.props.location.pathname,
        query: {
          ...this.props.query,
          error: undefined, // delete from query params and url
        },
      });
    }

    return Promise.all([
      this.props.fetchInstances(this.props.service, queryParams),
      this.props.fetchAllTeams({ organizationId: window.user?.organizationId }),
    ]);
  }

  componentDidUpdate(prevProps: ServiceImportProps) {
    if (prevProps.service !== this.props.service) {
      return this.props.fetchInstances(this.props.service);
    }

    // handle import status changes
    if (
      prevProps.importStatus.successfullyImportedRepoGroups.length !==
      this.props.importStatus.successfullyImportedRepoGroups.length
    ) {
      return this.handleImport();
    }

    // if instances change, reload filters
    if (!_.isEqual(prevProps.instances, this.props.instances)) {
      return this.props.fetchFilters(this.props.service, this.props.query.instanceName);
    }

    // if query parameters change, load filters
    if (!_.isEqual(prevProps.query, this.props.query)) {
      this.context.router.replace({
        pathname: this.props.location.pathname,
        query: this.props.query,
      });

      // if they are loading a different instance, reload filters
      if (prevProps.query.instanceName !== this.props.query.instanceName) {
        return this.props.fetchFilters(this.props.service, this.props.query.instanceName);
      }

      // if the search value has changed, debounce repo loading
      if (prevProps.query.q !== this.props.query.q) {
        return this.debounceFetch(this.props.service, this.props.query);
      }

      // otherwise load just repos
      return this.props.fetchRepositories(this.props.service, this.props.query);
    }

    // load repos if filters change
    if (!_.isEqual(prevProps.filters, this.props.filters)) {
      return this.props.fetchRepositories(this.props.service, this.props.query);
    }

    return null;
  }

  componentWillUnmount() {
    this.props.resetImportStatus();
  }

  debounceFetch = _.debounce(this.props.fetchRepositories, FETCH_REPOS_DEBOUNCE_TIMEOUT);

  onSearchChange = (event: ChangeEvent<HTMLInputElement>) =>
    this.props.setQueryParameters({
      ...this.props.query,
      q: event.target.value,
      page: 1,
    });

  onPageUp = () => {
    if (this.props.repositories.length < this.props.query.pageSize) {
      return;
    }
    this.props.setQueryParameters({
      ...this.props.query,
      page: this.props.query.page + 1,
    });
  };

  onPageDown = () => {
    if (this.props.query.page <= 1) {
      return;
    }
    this.props.setQueryParameters({
      ...this.props.query,
      page: this.props.query.page - 1,
    });
  };

  onSelectFilter = (filterType: string, filterValue: string) => {
    if (this.props.query.filterValue !== filterValue || this.props.query.filterType !== filterType) {
      this.props.setFilter(filterType, filterValue);
    }
  };

  onSelectInstanceName = (instanceName: string) => {
    if (this.props.query.instanceName === instanceName) {
      return;
    }

    this.props.setInstance(instanceName);
  };

  onDisconnect = async () => {
    await this.props.disconnectService(this.props.service, this.props.query.instanceName);
    window.location.reload();
  };

  handleImport = () => {
    const areAnyQueued = this.props.importStatus.successfullyImportedRepoGroups.some(repo => (repo.queued || 0) >= 1);
    // if only 1 repo imported, navigate to that project page
    const selectedRepo = this.props.selectedRepositories[0];
    // Unless two things are true.
    // First, hybrid projects are enabled.
    const areHybridProjectsEnabled = isFeatureEnabled(HYBRID_PROJECTS);
    // Second, this is a hybrid project that could be connected to more than one plausible other project.
    const isSelectedRepoUniquelyConnectable = (selectedRepo?.connectableProjects?.length ?? 0) <= 1;
    if (
      !areAnyQueued
      && this.props.importStatus.totalRepoCount === 1
      && selectedRepo
      && (!areHybridProjectsEnabled || isSelectedRepoUniquelyConnectable)
    ) {
      const locator = new Locator(
        isSelectedRepoUniquelyConnectable
          ? selectedRepo.connectableProjects?.[0] as string
          : selectedRepo.locator as string
      );
      this.context.router.push(`/projects/${utils.nuc(locator.toPackageString())}`);
    } else if (!this.props.importStatus.isBulkImport) {
      this.context.router.push({
        pathname: '/projects/',
      });
    }

    return null;
  };

  getAuthorizeUrl = ({ service, nextUrl, privateAccess, serviceConnect }: AuthorizeUrlParams): string =>
    getAuthorizeServiceURL({
      vcsHost: service,
      location: nextUrl,
      isPrivateAccess: privateAccess,
      serviceConnect,
      instanceName: this.props.query.instanceName,
    });

  hardRedirectToAuthorizeURL = ({ service, nextUrl, privateAccess, serviceConnect }: AuthorizeUrlParams) => {
    utils.hardRedirectTo(
      this.getAuthorizeUrl({ service, nextUrl, privateAccess, serviceConnect }),
    );
  };

  onGrantPrivateGithubAccess = () => this.hardRedirectToAuthorizeURL({
    service: 'github',
    nextUrl: window.location.href,
    privateAccess: true,
  });

  onImport = async (options: ProjectImportOptions, selectedRepos?: Repository[]) =>
    this.props.importRepositories(
      this.props.service,
      selectedRepos || this.props.selectedRepositories,
      {
        ...options,
        selectedTeams: options.selectedTeams || this.state.selectedTeams,
        send_badge_pr: options.send_badge_pr || this.state.send_badge_pr,
      },
      this.props.query.instanceName,
      this.props.query.filterValue,
    );

  onNonBulkImport = async () => {
    if (teamsUserCanAddProject(this.props.allTeams).length === 0) {
      await this.onImport({ selectedTeams: [] });
      return;
    }

    this.setState({ selectedTeams: [], showTeamSelectionModal: true });
  };

  onTeamsSelected = (teams: Team[]) => {
    this.setState({ selectedTeams: teams });
  };

  teamAssignmentClosed = () => {
    this.setState({ showTeamSelectionModal: false });
  };

  onTeamsSelectionImport = () => {
    this.teamAssignmentClosed();
    return this.onImport({} as ProjectImportOptions);
  };

  onRowRedirect = (projectId: string) => {
    this.context.router.push(`/projects/${utils.nuc(projectId)}`);
  };

  onBulkConfirm = () => {
    this.context.router.push('/projects');
  };

  onConnectToService = () => {
    const service = this.props.service.toLowerCase();
    if (
      // Don't give the user the option to connect the service for auth if the user's organization requires SSO.
      !window.user?.organization?.disable_invite && (
        (service === 'github' && !window.user?.github) ||
        (service === 'bitbucket_cloud' && !window.user?.bitbucketCloud) ||
        (service === 'gitlab' && !window.user?.gitlab)
      )
    ) {
      this.setState({ showConnectModal: true });
    } else {
      this.hardRedirectToAuthorizeURL({
        service: this.props.service,
        nextUrl: window.location.href,
      });
    }
  };

  isGithubApp = () => this.props.service === 'github-app';

  render() {
    // Unlike other quick imports, the Github App has an extra step to install the app.
    // Before the user installs the app, we should not display the PR option to avoid confusion.
    const githubAppInstallationStatus = hasGithubAppInstallation({
      isGithubApp: this.isGithubApp(),
      filters: this.props.filters,
    });
    const shouldDisplayBadgePRCheckbox = githubAppInstallationStatus !== 'missing';
    const serviceDetails = ServiceMeta[this.props.service as ServiceVCS];
    const serviceDisplayName = serviceDetails.displayName;

    if (this.props.status.loading) {
      return <Loader />;
    }

    if (this.props.importStatus.isBulkImport) {
      return (
        <div className="row fossa-service-import">
          <div className="col-md-6 col-md-offset-3">
            <div className="card card-primary text-center">
              <h2>Importing {this.props.selectedRepositories.length} Projects</h2>
              <p>This may take several minutes...</p>
              <BSButton onClick={this.onBulkConfirm} className="btn btn-cta" bsStyle="primary" bsSize="small">
                <IconText
                  icon={icons.arrowLeft}
                  text="Back to projects"
                />
              </BSButton>
            </div>
          </div>
        </div>
      );
    }

    if (this.props.importStatus.loading) {
      const projectNoun = this.props.selectedRepositories.length === 1 ? 'Project' : 'Projects';
      const importProgress = Math.floor(
        (this.props.importStatus.importGroupsCompleted / this.props.importStatus.importGroupCount) * 100,
      );
      return (
        <div className="row fossa-service-import">
          <div className="col-md-6 col-md-offset-3">
            <div className="card card-primary text-center">
              <Loader color="#d2b690" size={80} />
              <h2>Importing {this.props.selectedRepositories.length} {projectNoun}</h2>
              <p>This may take a few minutes...</p>
              {this.props.importStatus.importGroupCount > 2 &&
                <ProgressBar style={{ marginTop: '15px' }} now={importProgress} label={`${importProgress}%`} />}
            </div>
          </div>
        </div>
      );
    }

    const selectedFilter = _.find(
      _.flatMap(this.props.filters, (filter) => filter.filters),
      (filter) => filter.value === this.props.query.filterValue,
    );

    return (
      <div className="fossa-service-import">
        <div className="panel">
          <div className="panel-body padded padded-small">
            <ServiceImportHeader
              serviceDetails={serviceDetails}
              selectedFilter={selectedFilter}
              filters={this.props.filters}
              query={this.props.query}
              instanceNames={this.props.instances}
              isLoading={this.props.status.loading || this.props.importStatus.loading}
              selectedRepoLength={this.props.selectedRepositories.length}
              onSelectInstanceName={this.onSelectInstanceName}
              onImport={this.onNonBulkImport}
              onBulkImport={() => this.setState({ show_bulk_import: true })}
              onSelectFilter={this.onSelectFilter}
              onSearchChange={this.onSearchChange}
              instanceIsConnected={this.props.connected}
              searchValue={this.props.query.q}
              onConnectToService={this.onConnectToService}
            />
          </div>
        </div>
        <div className="row">
          <div className="col-xs-3">
            {(serviceDetails.authNote || serviceDetails.docsURL) && (
              <div className="card card-flat">
                <IconText icon={icons.questionCircle} text={serviceDetails.authNote as string} />
                {serviceDetails.docsURL && (
                  <Link className="btn btn-default btn-block margin-top" to={serviceDetails.docsURL} openInNewTab>
                    <span className={styles.docsLink}>
                      <IconText icon={icons.book} text={serviceDetails.docsLinkText || 'Documentation'} />
                    </span>
                  </Link>
                )}
              </div>
            )}
            {this.props.connected && (
              <>
                <Button
                  onClick={this.onDisconnect}
                  type={BUTTON_TYPE.ERROR}
                  size={BUTTON_SIZE.SMALL}
                  isDisabled={this.props.importStatus.loading}
                  icon={icons.powerOff}
                >
                  Disconnect from {serviceDisplayName}
                </Button>
                {this.props.instances.length > 1 && (
                  <p className="margin-top-md font-size-sm">Note: this will only disconnect the current instance</p>
                )}
                {shouldDisplayBadgePRCheckbox && (
                  <Checkbox
                    className="margin-top-xl"
                    checked={this.state.send_badge_pr}
                    onChange={() => {
                      this.setState((prevState) => ({ send_badge_pr: !prevState.send_badge_pr }));
                    }}
                  >
                    Submit badge PRs after import (public GitHub READMEs only)
                  </Checkbox>
                )}
              </>
            )}
          </div>
          <div className="col-xs-9">
            <div className="row">
              <div className="col-xs-12">
                {this.state.errorFromQueryParameters || this.props.status.error ? (
                  <div className="alert alert-error">
                    {this.state.errorFromQueryParameters || this.props.status.error}
                  </div>
                ) : null}
              </div>
            </div>
            {this.props.service === 'github' && this.props.connected && this.props.privateAccess === false && (
              <div className="alert alert-warning clickable" onClick={this.onGrantPrivateGithubAccess}>
                FOSSA is showing only <b>public repositories</b>. &nbsp;&nbsp;
                <b>
                  <u>Click here</u>
                </b>{' '}
                to grant private repo access.
              </div>
            )}
            {this.props.status.loading ? (
              <Loader />
            ) : (
              <ServiceImportRespositories
                repositories={this.props.repositories}
                selectedRepos={this.props.selectedRepositories}
                page={this.props.query.page}
                pageSize={this.props.query.pageSize}
                isConnected={this.props.connected}
                isLoading={this.props.status.loading}
                error={this.props.status.error}
                onPageUp={this.onPageUp}
                onPageDown={this.onPageDown}
                onBranchChange={this.props.setBranch}
                onRowRedirect={this.onRowRedirect}
                onSelectRepository={this.props.selectRepository}
                onConnectToService={this.onConnectToService}
              />
            )}
            <ModalBulkImport
              service={this.props.service}
              filterName={selectedFilter?.name}
              filterType={this.props.query.filterType}
              filterValue={this.props.query.filterValue}
              instanceName={this.props.query.instanceName}
              show={this.state.show_bulk_import}
              onHide={() => this.setState({ show_bulk_import: false })}
              onFinish={(selectedRepositories: Repository[], options: ProjectImportOptions) => {
                selectedRepositories.forEach((r) => this.props.selectRepository(r));
                return this.onImport(options, selectedRepositories);
              }}
            />
            <ModalTeamAssignment
              show={this.state.showTeamSelectionModal}
              onHide={this.teamAssignmentClosed}
              allTeams={this.props.allTeams}
              selectedTeams={this.state.selectedTeams}
              onTeamsSelected={this.onTeamsSelected}
              onTeamsSelectionImport={this.onTeamsSelectionImport}
            />
          </div>
        </div>
        <ModalServiceConnect
          show={this.state.showConnectModal}
          serviceDisplayName={serviceDisplayName}
          service={this.props.service}
          hardRedirectToAuthorizeURL={this.hardRedirectToAuthorizeURL}
        />
      </div>
    );
  }
}

const mapStateToProps = (state: ServiceImportState) => ({
  allTeams: TeamsModule.selectors.getTeamList(state),
  instances: VCSModule.selectors.getInstances(state),
  filters: VCSModule.selectors.getFilters(state),
  repositories: VCSModule.selectors.getRepositories(state),
  selectedRepositories: VCSModule.selectors.getSelectedRepositories(state),
  privateAccess: VCSModule.selectors.getIsPrivateAccess(state),
  connected: VCSModule.selectors.getIsConnected(state),
  status: VCSModule.selectors.getStatus(state),
  importStatus: VCSModule.selectors.getImportStatus(state),
  query: VCSModule.selectors.getQueryParameters(state),
});

const mapDispatchToProps = {
  fetchAllTeams: TeamsModule.actions.fetchAll,
  fetchInstances: VCSModule.actions.fetchInstances,
  fetchFilters: VCSModule.actions.fetchFilters,
  fetchRepositories: VCSModule.actions.fetchRepositories,
  importRepositories: VCSModule.actions.importRepositories,
  resetImportStatus: VCSModule.actions.resetImportStatus,
  setQueryParameters: VCSModule.actions.setQueryParameters,
  setInstance: VCSModule.actions.setInstance,
  setFilter: VCSModule.actions.setFilter,
  setBranch: VCSModule.actions.setBranchOnRepository,
  selectRepository: VCSModule.actions.selectRepository,
  disconnectService: VCSModule.actions.disconnectService,
};

export default connect(mapStateToProps, mapDispatchToProps)(ServiceImport);
